import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';

export const METRIC_LABELS = {
    orders_units: 'Заказы, ед',
    orders_revenue: 'Заказы, руб',
    conversion: 'Конверсии',
    ctr: 'CTR',
    reach: 'Охваты',
    all: 'Все показатели',
    orders: 'Заказы',
    revenue: 'Выручка',
};

export const METRIC_COLORS = {
    orders_units: '#1d4ed8',
    orders_revenue: '#15803d',
    conversion: '#9333ea',
    ctr: '#ea580c',
    reach: '#0f766e',
    all: '#4b5563',
    orders: '#1d4ed8',
    revenue: '#15803d',
};

function parseSkuText(raw) {
    return Array.from(
        new Set(
            String(raw || '')
                .split(/[\n,;\t\r ]+/)
                .map((s) => s.trim())
                .filter(Boolean)
        )
    );
}

function normalizeId(value) {
    return String(value || '').replace(/-/g, '').toLowerCase();
}

export default function TaskResultLinksSection({
    taskId,
    taskWorkspaceId,
    visibleWorkspaces = [],
    marketplaces = [],
    cabinetIds = [],
}) {
    const { request, showStatus } = useApi();

    const [loading, setLoading] = useState(false);
    const [catalog, setCatalog] = useState([]);

    const workspace = useMemo(
        () => visibleWorkspaces.find((w) => normalizeId(w.id) === normalizeId(taskWorkspaceId)),
        [visibleWorkspaces, taskWorkspaceId]
    );
    const workspaceCabinets = useMemo(() => {
        const byId = new Map((marketplaces || []).map((m) => [normalizeId(m.id), m]));
        return (workspace?.cabinets || [])
            .map((cabinet) => {
            const id = String(cabinet.marketplace_account_id || '');
            const account = byId.get(normalizeId(id));
            if (!account) return null;
            return {
                id,
                account_name: account?.account_name || id,
                marketplace_type: account?.marketplace_type || '',
            };
        })
            .filter(Boolean);
    }, [workspace, marketplaces]);
    const workspaceCabinetsKey = useMemo(
        () => workspaceCabinets.map((item) => normalizeId(item.id)).sort().join(','),
        [workspaceCabinets]
    );

    // Привязка к результату может ссылаться только на кабинет, выбранный для самой
    // задачи — иначе можно привязать результат к кабинету, которого в задаче нет.
    const resultCabinetOptions = useMemo(() => {
        const selected = new Set((cabinetIds || []).map((id) => normalizeId(id)));
        return workspaceCabinets.filter((m) => selected.has(normalizeId(m.id)));
    }, [workspaceCabinets, cabinetIds]);

    const [cabinetId, setCabinetId] = useState('');
    const [scope, setScope] = useState('assortment');
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [skuSearch, setSkuSearch] = useState('');
    const [skuResults, setSkuResults] = useState([]);
    const [skuPaste, setSkuPaste] = useState('');
    const [showPaste, setShowPaste] = useState(false);
    const [searchBusy, setSearchBusy] = useState(false);
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [saving, setSaving] = useState(false);
    const searchWrapRef = useRef(null);
    const pasteWrapRef = useRef(null);

    const metricOptions = useMemo(
        () => catalog.flatMap((g) => g.options || []),
        [catalog]
    );

    useEffect(() => {
        if (!taskId) return;
        const run = async () => {
            setLoading(true);
            try {
                const [catalogRes, bindingRes] = await Promise.all([
                    request('/api/v1/tasks/metric-options'),
                    request(`/api/v1/tasks/${taskId}/result-binding`),
                ]);
                const groups = catalogRes?.groups || [];
                setCatalog(groups);
                const bindingCabinetId = bindingRes?.marketplace_account_id || '';
                const hasCabinetInTask = resultCabinetOptions.some(
                    (item) => normalizeId(item.id) === normalizeId(bindingCabinetId)
                );
                setCabinetId(hasCabinetInTask ? bindingCabinetId : '');
                setScope(bindingRes?.scope || 'assortment');
                setSelectedSkus(bindingRes?.skus || []);
                setSelectedMetrics(bindingRes?.metrics || []);
            } catch (e) {
                showStatus('error', 'Не удалось загрузить привязку результата');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [taskId, workspaceCabinetsKey]);

    useEffect(() => {
        if (!cabinetId || !skuSearch.trim() || scope !== 'skus') {
            setSkuResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setSearchBusy(true);
            try {
                const results = await request(
                    `/api/v1/marketplaces/${cabinetId}/products?q=${encodeURIComponent(skuSearch)}&limit=20`
                );
                setSkuResults(results || []);
            } catch (e) {
                setSkuResults([]);
                showStatus('error', `Ошибка поиска артикулов. ${e?.message || ''}`.trim());
            } finally {
                setSearchBusy(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [skuSearch, cabinetId, scope]);

    useEffect(() => {
        if (scope !== 'skus') return;
        if (cabinetId) return;
        if (resultCabinetOptions.length === 1) {
            setCabinetId(resultCabinetOptions[0].id);
        }
    }, [scope, cabinetId, resultCabinetOptions]);

    // Если из списка кабинетов задачи убрали выбранный кабинет привязки — сбрасываем привязку,
    // не дожидаясь пересохранения (без лишнего запроса к серверу).
    useEffect(() => {
        if (!cabinetId) return;
        if (resultCabinetOptions.some((m) => normalizeId(m.id) === normalizeId(cabinetId))) return;
        setCabinetId('');
        setScope('assortment');
        setSelectedSkus([]);
    }, [resultCabinetOptions, cabinetId]);

    useEffect(() => {
        const handleClick = (e) => {
            if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) {
                setSkuResults([]);
            }
            if (pasteWrapRef.current && !pasteWrapRef.current.contains(e.target)) {
                setShowPaste(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const addSku = (product) => {
        const sku = String(product?.article || product?.sku || '').trim();
        if (!sku) return;
        if (!selectedSkus.includes(sku)) {
            setSelectedSkus((prev) => [...prev, sku]);
        }
        setSkuSearch('');
        setSkuResults([]);
    };

    const addPastedSkus = () => {
        const parsed = parseSkuText(skuPaste);
        if (!parsed.length) return;
        setSelectedSkus((prev) => Array.from(new Set([...prev, ...parsed])));
        setSkuPaste('');
    };

    const removeSku = (sku) => setSelectedSkus((prev) => prev.filter((s) => s !== sku));

    const toggleMetric = (key) => {
        setSelectedMetrics((prev) => {
            if (key === 'all') {
                return prev.includes('all') ? [] : ['all'];
            }
            const withoutAll = prev.filter((k) => k !== 'all');
            return withoutAll.includes(key)
                ? withoutAll.filter((k) => k !== key)
                : [...withoutAll, key];
        });
    };

    const handleSave = async () => {
        if (!cabinetId) return;
        if (!selectedMetrics.length) return;
        if (scope === 'skus' && (!cabinetId || !selectedSkus.length)) return;
        setSaving(true);
        try {
            const payload = {
                marketplace_account_id: cabinetId || null,
                scope,
                skus: scope === 'skus' ? selectedSkus : [],
                metrics: selectedMetrics,
            };
            const updated = await request(`/api/v1/tasks/${taskId}/result-binding`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
            setCabinetId(updated?.marketplace_account_id || '');
            setScope(updated?.scope || 'assortment');
            setSelectedSkus(updated?.skus || []);
            setSelectedMetrics(updated?.metrics || []);
            showStatus('success', 'Привязка результата сохранена');
        } catch (e) {
            showStatus('error', `Ошибка сохранения привязки результата. ${e?.message || ''}`.trim());
        } finally {
            setSaving(false);
        }
    };

    const canSave =
        !loading &&
        !!cabinetId &&
        selectedMetrics.length > 0 &&
        (scope === 'assortment' || (scope === 'skus' && cabinetId && selectedSkus.length > 0));

    return (
        <div className="result-links">
            <div className="result-links__head">
                <span className="result-links__title">Привязка к результату</span>
                {workspace && <span className="result-links__project-badge">{workspace.name}</span>}
            </div>

            {!taskWorkspaceId && (
                <p className="result-links__no-workspace">
                    Назначьте проект задаче, чтобы добавить привязки к результату.
                </p>
            )}

            {taskWorkspaceId && (
                <div className="result-links__form">
                    <div className="result-links__row">
                        <select
                            value={cabinetId}
                            onChange={(e) => setCabinetId(e.target.value)}
                            className="result-links__select"
                        >
                            <option value="">Выберите кабинет</option>
                            {resultCabinetOptions.map((m) => (
                                <option key={m.id} value={m.id}>
                                    {m.account_name}
                                    {m.marketplace_type === 'wildberries' ? ' (WB)' : ' (OZON)'}
                                </option>
                            ))}
                        </select>
                        {resultCabinetOptions.length === 0 && (
                            <p className="result-links__sku-hint">
                                Сначала выберите кабинет(ы) для задачи — привязать результат можно
                                только к одному из них.
                            </p>
                        )}
                    </div>

                    <div className="result-links__scope">
                        <label className="result-links__radio">
                            <input
                                type="radio"
                                name={`rl-scope-${taskId}`}
                                value="assortment"
                                checked={scope === 'assortment'}
                                onChange={() => setScope('assortment')}
                                disabled={!cabinetId}
                            />
                            Весь ассортимент
                        </label>
                        <label className="result-links__radio">
                            <input
                                type="radio"
                                name={`rl-scope-${taskId}`}
                                value="skus"
                                checked={scope === 'skus'}
                                onChange={() => setScope('skus')}
                                disabled={!cabinetId}
                            />
                            Группа артикулов
                        </label>
                    </div>

                    {scope === 'skus' && (
                        <div className="result-links__sku-picker">
                            {!cabinetId ? (
                                <p className="result-links__sku-hint">
                                    Выберите конкретный кабинет для поиска артикулов
                                </p>
                            ) : (
                                <div className="result-links__sku-search-wrap" ref={searchWrapRef}>
                                    <input
                                        type="text"
                                        className="result-links__sku-input"
                                        value={skuSearch}
                                        onChange={(e) => setSkuSearch(e.target.value)}
                                        placeholder="Поиск артикула или названия товара..."
                                    />
                                    {searchBusy && <span className="result-links__sku-busy">Поиск...</span>}
                                    {skuResults.length > 0 && (
                                        <ul className="result-links__sku-results">
                                                {skuResults.map((p) => (
                                                <li
                                                    key={p.article || p.sku}
                                                    className="result-links__sku-result"
                                                    onMouseDown={() => addSku(p)}
                                                >
                                                    <b>{p.article || p.sku}</b>
                                                    {p.vendor_article ? ` (арт. ${p.vendor_article})` : ''}
                                                    {p.name ? ` - ${p.name}` : ''}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            <div className="result-links__row" style={{ marginTop: 8 }} ref={pasteWrapRef}>
                                <button
                                    type="button"
                                    className="result-links__add-btn"
                                    onClick={() => setShowPaste((prev) => !prev)}
                                >
                                    Вставить столбец артикулов
                                </button>
                                {showPaste && (
                                    <div className="result-links__sku-results" style={{ width: '100%', maxHeight: 'none', position: 'static' }}>
                                        <textarea
                                            rows={3}
                                            className="result-links__sku-input"
                                            value={skuPaste}
                                            onChange={(e) => setSkuPaste(e.target.value)}
                                            placeholder="Артикулы по одному в строке или через разделитель"
                                        />
                                        <button
                                            type="button"
                                            className="result-links__add-btn"
                                            onClick={addPastedSkus}
                                            style={{ marginTop: 8 }}
                                        >
                                            Добавить артикулы
                                        </button>
                                    </div>
                                )}
                            </div>

                            {selectedSkus.length > 0 && (
                                <div className="result-links__sku-selected">
                                    {selectedSkus.map((sku) => (
                                        <span key={sku} className="result-links__sku-tag" title={sku}>
                                            {sku}
                                            <button type="button" onClick={() => removeSku(sku)}>
                                                x
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="result-links__metrics">
                        <span className="result-links__metrics-label">Показатели:</span>
                        <div className="result-links__metrics-pills">
                            {metricOptions.map((option) => {
                                const key = option.key;
                                const label = option.label || METRIC_LABELS[key] || key;
                                const active = selectedMetrics.includes(key);
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        className={`result-links__metric-pill${active ? ' active' : ''}`}
                                        style={
                                            active
                                                ? {
                                                      borderColor: METRIC_COLORS[key] || '#6b7280',
                                                      background: `${METRIC_COLORS[key] || '#6b7280'}22`,
                                                      color: METRIC_COLORS[key] || '#6b7280',
                                                  }
                                                : {}
                                        }
                                        onClick={() => toggleMetric(key)}
                                        disabled={!cabinetId}
                                    >
                                        <span
                                            className="result-links__metric-dot"
                                            style={{ background: METRIC_COLORS[key] || '#6b7280' }}
                                        />
                                        {label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="result-links__add-btn"
                        onClick={handleSave}
                        disabled={saving || !canSave}
                    >
                        {saving ? 'Сохранение...' : 'Сохранить привязку'}
                    </button>

                    <div className="result-links__empty" style={{ textAlign: 'left' }}>
                        Правило: у задачи один общий набор показателей для всего выбранного ассортимента.
                    </div>
                </div>
            )}
        </div>
    );
}
